// packages/i18n/src/shared/utils/matchUrlPatterns.ts
import { patternToRegex } from './patternToRegex';

export interface UrlPatternMatch {
  code: string;
  /** The values the pathname gave the pattern's `:param`s. Empty for a literal pattern. */
  params: Record<string, string>;
}

/**
 * The codes of ONE locale's URL map whose pattern matches `pathname`, best first:
 * the literal pattern (no `:param`) that IS the pathname, then the parametric
 * patterns in map order.
 *
 * Literal first, because a page beside a parametric sibling also satisfies the
 * sibling's pattern, with its own last segment read as the id: `/tours/manage/new`
 * beside `/tours/manage/:tourId`, `/economy/billing/pay-all` beside
 * `/economy/billing/:invoiceId`. Taking whichever comes first in the map titled
 * such a page as its sibling, and translated its path into another language as
 * `…/:id` with a word of the first language for id, which opens the sibling's page
 * for an id that does not exist. The proxy's rewrite of a localized path applies
 * the same order (apps/backoffice/src/proxy.ts).
 */
export function matchUrlPatterns(
  localeMap: Readonly<Record<string, string>>,
  pathname: string,
): UrlPatternMatch[] {
  const entries = Object.entries(localeMap);
  const matches: UrlPatternMatch[] = [];

  for (const [code, pattern] of entries) {
    if (!pattern.includes(':') && pattern === pathname) matches.push({ code, params: {} });
  }

  for (const [code, pattern] of entries) {
    if (!pattern.includes(':')) continue;
    const { regex, keys } = patternToRegex(pattern);
    const match = regex.exec(pathname);
    if (!match) continue;
    matches.push({ code, params: Object.fromEntries(keys.map((key, index) => [key, match[index + 1]])) });
  }

  return matches;
}
